import {StructureBuilder, StructureResolverContext} from 'sanity/structure'
import {BiFileBlank} from 'react-icons/bi'
import {FiAlertCircle} from 'react-icons/fi'
import {map} from 'rxjs/operators'

export default function postsStructure(S: StructureBuilder, context: StructureResolverContext) {
  const postSchemaType = 'blog.post'
  const clusterSchemaType = 'blog.category-cluster'
  const categorySchemaType = 'blog.category'

  const client = context.getClient({apiVersion: '2025-01-01'})

  return S.listItem()
    .title('Posts')
    .icon(BiFileBlank)
    .child(
      S.list()
        .title('Posts')
        .items([
          // All posts
          S.listItem()
            .title('All Posts')
            .icon(BiFileBlank)
            .child(
              S.documentList()
                .title('All Posts')
                .schemaType(postSchemaType)
                .filter(`_type == $type && !(_id in path("drafts.**"))`)
                .params({type: postSchemaType}),
            ),

          // Draft posts - using custom list to show only drafts
          S.listItem()
            .title('Draft Posts')
            .icon(BiFileBlank)
            .child(() =>
              client.observable
                .fetch(
                  `*[_type == $type && _id in path("drafts.**")] | order(_updatedAt desc) {
                    _id,
                    _type,
                    title,
                    _updatedAt
                  }`,
                  {type: postSchemaType},
                )
                .pipe(
                  map((drafts) =>
                    S.list()
                      .title('Draft Posts')
                      .items(
                        drafts.map((draft: any) =>
                          S.documentListItem()
                            .id(draft._id)
                            .schemaType(postSchemaType)
                            .title(draft.title || 'Untitled'),
                        ),
                      ),
                  ),
                ),
            ),

          S.listItem()
            .title('Posts with wrong featured media')
            .icon(FiAlertCircle)
            .child(
              S.documentList()
                .title('Posts with wrong featured media')
                .schemaType(postSchemaType)
                .filter(`_type == $type && !defined(featuredMedia)`)
                .params({type: postSchemaType}),
            ),

          S.listItem()
            .title('Posts with wrong category')
            .icon(FiAlertCircle)
            .child(
              S.documentList()
                .title('Posts with wrong category')
                .schemaType(postSchemaType)
                .filter(`_type == $type && mainCategory->_type == "${clusterSchemaType}"`)
                .params({type: postSchemaType}),
            ),

          S.listItem()
            .title('Posts without tags')
            .icon(FiAlertCircle)
            .child(
              S.documentList()
                .title('Posts without tags')
                .schemaType(postSchemaType)
                .filter(`_type == $type && count(tags) == 0`)
                .params({type: postSchemaType}),
            ),

          S.divider({id: 'divider-by-cluster', type: 'divider', title: 'By Cluster'}),

          // Posts by cluster - following Sanity pattern
          S.listItem()
            .title('Posts by cluster')
            .icon(BiFileBlank)
            .child(
              S.documentList()
                .title('Category Clusters')
                .schemaType(clusterSchemaType)
                .filter(`_type == "${clusterSchemaType}"`)
                .child((clusterId) =>
                  S.documentList()
                    .title('Posts')
                    .schemaType(postSchemaType)
                    .filter(
                      `_type == "${postSchemaType}" && mainCategory->cluster._ref == $clusterId`,
                    )
                    .params({clusterId}),
                ),
            ),

          S.listItem()
            .title('Posts by category')
            .icon(BiFileBlank)
            .child(
              S.documentList()
                .title('Category')
                .schemaType(categorySchemaType)
                .filter(`_type == "${categorySchemaType}"`)
                .defaultOrdering([{field: 'title', direction: 'asc'}])
                .child((categoryId) =>
                  S.documentList()
                    .title('Posts')
                    .schemaType(postSchemaType)
                    .filter(`_type == "${postSchemaType}" && mainCategory._ref == $categoryId`)
                    .params({categoryId}),
                ),
            ),
        ]),
    )
}
